import { type Common } from './common.js';
import {
  type AgentConversation,
  type AgentConversationPopulated,
  type AgentConversationQuery,
  type AgentConversationVerbose,
  type AgentConversationWithTags,
  type UpdateValues,
} from './models/agentConversation.js';
import { type ChatMessage, type ChatSessionWithMessages } from './models/assistant/index.js';
import { SortOrder } from './models/common.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/agent-conversations';
  }

  async getAgentConversation(agentConversationId: string): Promise<{
    agentConversation: AgentConversationVerbose;
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${agentConversationId}`);
    return await this.common.getJSON(url);
  }

  async getAgentConversations(query: AgentConversationQuery): Promise<{
    agentConversations: AgentConversationPopulated[];
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, query);
    return await this.common.getJSON(url);
  }

  async getAgentConversationPriorities(query: {
    organizationIds: string[];
  }): Promise<{ priorities: string[] }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/priorities`, query);
    return await this.common.getJSON(url);
  }

  async getMessages(options: {
    agentConversationId: string;
    orderBy?: string;
    orderByDirection?: SortOrder;
  }): Promise<{ chatSession: ChatSessionWithMessages }> {
    const {
      agentConversationId,
      orderBy = 'created',
      orderByDirection = SortOrder.Ascending,
    } = options;
    const url = this.common.constructPath(`${this.baseEndpoint}/${agentConversationId}/messages`, {
      orderBy,
      orderByDirection,
    });
    return await this.common.getJSON(url);
  }

  async patchAgentConversation(options: {
    agentConversationId: string;
    updateValues: UpdateValues;
  }): Promise<{ agentConversation: AgentConversationWithTags }> {
    const { agentConversationId, updateValues } = options;
    const url = this.common.constructPath(`${this.baseEndpoint}/${agentConversationId}`);
    return await this.common.patchJSON(url, updateValues);
  }

  async postMessage(options: { agentConversationId: string; message: string }): Promise<{
    agentConversation: AgentConversation;
    chatMessage: ChatMessage;
  }> {
    const { agentConversationId, message } = options;
    const url = this.common.constructPath(`${this.baseEndpoint}/${agentConversationId}/messages`);
    return await this.common.postJSON(url, { message });
  }

  async reopenAgentConversation(options: {
    existingConversationId: string;
  }): Promise<{ agentConversation: AgentConversationWithTags }> {
    const { existingConversationId } = options;
    const url = this.common.constructPath(`${this.baseEndpoint}/${existingConversationId}/reopen`);
    return await this.common.postJSON(url, options);
  }
}
